import React, { useContext, useEffect } from "react";
import { Stack, Group, Text } from "@mantine/core";
import FloatingLabelInput from "./floating-label-input";
import { DataContext } from "../context/DataContext";
import useLocale from "../hooks/useLocale";

/**
 * German address block: street + house number, 5-digit PLZ, city, and an
 * optional Bundesland. Writes the same addr/city/state/zip keys as the US
 * and UK inputs so the AgeMatch payload shape is unchanged; `state` is
 * simply "" when the user leaves Bundesland empty.
 */
const DeAddressInput = ({
  onAddressChange,
  value,
  enterKeyHint,
  disabled = false,
  ...props
}) => {
  const { text, data } = useContext(DataContext);
  const locale = useLocale("agematch");
  const labels = locale?.address?.de || {};

  const safeValue = value && typeof value === "object" ? value : {};
  const address = {
    addr: safeValue.addr ?? data?.addr ?? "",
    city: safeValue.city ?? data?.city ?? "",
    state: safeValue.state ?? data?.state ?? "",
    zip: safeValue.zip ?? data?.zip ?? "",
  };

  const handleInputChange = (field, fieldValue) => {
    onAddressChange({ ...address, [field]: fieldValue });
  };

  const handlePlzChange = (raw) => {
    // PLZ is always exactly five digits; keep the field numeric.
    handleInputChange("zip", String(raw || "").replace(/\D/g, "").slice(0, 5));
  };

  // Seed the parent's form values once on mount so navigating away and
  // back never clears the inputs (same as AddressInput / UkAddressInput).
  useEffect(() => {
    onAddressChange(address);
  }, []);

  return (
    <>
      <Group>
        <Text fz="xs" c="dimmed">
          {text?.general?.address || locale?.address?.label || "Address"}
        </Text>
      </Group>

      <Stack spacing="xs">
        {props.error && (
          <Text fz="xs" c="red">
            {locale?.form?.addressRequired || "Address is required"}
          </Text>
        )}
        <FloatingLabelInput
          placeholder=""
          label={labels.street || "Street and house number"}
          value={address.addr}
          onChange={(e) => handleInputChange("addr", e.currentTarget.value)}
          autoComplete="address-line1"
          enterKeyHint={enterKeyHint}
          disabled={disabled}
        />
        <FloatingLabelInput
          placeholder=""
          label={labels.plz || "Postal code (PLZ)"}
          value={address.zip}
          onChange={(e) => handlePlzChange(e.currentTarget.value)}
          autoComplete="postal-code"
          inputMode="numeric"
          type="tel"
          maxLength={5}
          enterKeyHint={enterKeyHint}
          disabled={disabled}
        />
        <FloatingLabelInput
          placeholder=""
          label={labels.city || "City"}
          value={address.city}
          onChange={(e) => handleInputChange("city", e.currentTarget.value)}
          autoComplete="address-level2"
          enterKeyHint={enterKeyHint}
          disabled={disabled}
        />
        <FloatingLabelInput
          placeholder=""
          label={labels.state || "State (optional)"}
          value={address.state}
          onChange={(e) => handleInputChange("state", e.currentTarget.value)}
          autoComplete="address-level1"
          enterKeyHint={enterKeyHint}
          disabled={disabled}
        />
      </Stack>
    </>
  );
};

export default DeAddressInput;
